import React, { useState } from 'react';
import { AlertTriangle, Info, RefreshCw, ChevronDown, ChevronRight, Wifi, WifiOff, Activity } from 'lucide-react';

/**
 * Connection Diagnostics Component
 * Displays detailed connection information and troubleshooting
 */
const ConnectionDiagnostics = ({ 
  connectionStatus = 'disconnected',
  diagnostics = {},
  error = null,
  onReconnect = null,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get connection health status
  const getHealthStatus = () => {
    if (connectionStatus === 'connected') return { level: 'good', color: 'green' };
    if (connectionStatus === 'degraded') return { level: 'warning', color: 'yellow' };
    if (connectionStatus === 'connecting') return { level: 'info', color: 'blue' };
    return { level: 'error', color: 'red' };
  };

  const health = getHealthStatus();

  // Common WebSocket issues and solutions
  const troubleshootingTips = {
    'Connection closed unexpectedly': [
      'Server may be rate limiting connections',
      'Check your internet connection stability',
      'Try refreshing the page',
      'Disable browser extensions that might block WebSockets'
    ],
    'Connection timeout': [
      'Server is not responding',
      'Check if the service is down',
      'Try again in a few moments',
      'Check your firewall settings'
    ],
    'Network connectivity issue': [
      'Check your internet connection',
      'Verify DNS resolution is working',
      'Try using a different network',
      'Contact your network administrator'
    ],
    'CORS policy blocking': [
      'This is a browser security restriction',
      'The application is configured correctly',
      'Try refreshing the page',
      'Contact support if the issue persists'
    ]
  };

  // Get relevant troubleshooting tips
  const getTroubleshootingTips = () => {
    if (!error) return [];
    
    const errorMessage = typeof error === 'string' ? error : error?.message || '';
    
    for (const [key, tips] of Object.entries(troubleshootingTips)) {
      if (errorMessage?.toLowerCase()?.includes(key?.toLowerCase())) {
        return tips;
      }
    }
    
    return [
      'Try refreshing the page',
      'Check your internet connection',
      'The app will use simulated data as fallback',
      'Contact support if issues persist'
    ];
  };

  const tips = getTroubleshootingTips();

  return (
    <div className={`bg-white rounded-lg border ${className}`}>
      {/* Header */}
      <div 
        className={`
          flex items-center justify-between p-4 cursor-pointer
          border-l-4 border-${health?.color}-400
        `}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          <div className={`text-${health?.color}-500`}>
            {connectionStatus === 'connected' && <Wifi className="w-5 h-5" />}
            {connectionStatus === 'connecting' && <Activity className="w-5 h-5 animate-pulse" />}
            {(connectionStatus === 'error' || connectionStatus === 'disconnected') && <WifiOff className="w-5 h-5" />}
            {connectionStatus === 'degraded' && <AlertTriangle className="w-5 h-5" />}
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900">Connection Status</h3>
            <p className={`text-sm text-${health?.color}-600 capitalize`}>
              {connectionStatus === 'degraded' ? 'Using Fallback Data' : connectionStatus}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Reconnect Button */}
          {onReconnect && (connectionStatus === 'error' || connectionStatus === 'disconnected') && (
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onReconnect();
              }}
              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
          
          {/* Expand Icon */}
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t bg-gray-50">
          <div className="p-4 space-y-4">
            {/* Error Information */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">Current Issue</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {typeof error === 'string' ? error : error?.message || 'Unknown error'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Connection Diagnostics */}
            {diagnostics && Object.keys(diagnostics)?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="w-full">
                    <h4 className="font-medium text-blue-800">Connection Details</h4>
                    <div className="mt-2 space-y-1 text-sm text-blue-700">
                      {diagnostics?.activeConnections !== undefined && (
                        <div>Active Connections: {diagnostics?.activeConnections}</div>
                      )}
                      {diagnostics?.connectionAttempts && (
                        <div>Connection Attempts: {diagnostics?.connectionAttempts}</div>
                      )}
                      {diagnostics?.cachedSymbols && (
                        <div>Cached Symbols: {diagnostics?.cachedSymbols}</div>
                      )}
                      {diagnostics?.fallbackActive && (
                        <div>Fallback Mode: Active</div>
                      )}
                      {diagnostics?.lastUpdate && (
                        <div>Last Update: {new Date(diagnostics?.lastUpdate)?.toLocaleTimeString()}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Troubleshooting Tips */}
            {tips?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <h4 className="font-medium text-yellow-800 mb-2">Troubleshooting Tips</h4>
                <ul className="space-y-1 text-sm text-yellow-700">
                  {tips?.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-yellow-500 mt-1">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Fallback Information */}
            {connectionStatus === 'degraded' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-800">Fallback Mode Active</h4>
                    <p className="text-sm text-green-700 mt-1">
                      The application is using simulated market data while attempting to restore 
                      live connections. All features remain functional with realistic demo data.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionDiagnostics;